import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

// Curated electro-mechanical imagery (verified loading on this site)
const PHOTOS = [
  // HVAC Plant Rooms — heavy industrial machinery
  "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Power Distribution — modern facility / MEP infrastructure
  "https://images.unsplash.com/photo-1646662521253-5b9253b1a207?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Control & Automation — MNH authentic PCB / control board photo
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/sl5xdsm6_Electro_Mechanical_Assembly_Manufacturing.jpg",
  // Fire Fighting Systems — protected modern building
  "https://images.unsplash.com/photo-1758798219572-512a03a60ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Cable Management — airport infrastructure with cabling
  "https://images.unsplash.com/photo-1774535140090-8f860e44dd0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Lighting & Power — solar / energy infrastructure
  "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

// Bento spans for visual rhythm — 12-col grid
const SPANS = [
  "md:col-span-7 md:row-span-2",   // HVAC — large
  "md:col-span-5",                 // Power
  "md:col-span-5",                 // Engineer
  "md:col-span-4",                 // Fire
  "md:col-span-4",                 // Cable
  "md:col-span-4",                 // Lighting
];
const HEIGHTS = [
  "h-[460px]",
  "h-[220px]",
  "h-[220px]",
  "h-[260px]",
  "h-[260px]",
  "h-[260px]",
];

export default function ElectroMechanicalGallery() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const g = t.emGallery;

  return (
    <section
      id="electro-mechanical"
      className="bg-secondary text-secondary-foreground grain"
      data-testid="em-gallery-section"
    >
      <div className="container-page section-y">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="overline text-primary">{g.eyebrow}</span>
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
              {g.heading1} <br />
              <span className="text-primary">{g.heading1Accent}</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-4 text-secondary-foreground/65 leading-relaxed"
          >
            {g.description}
          </motion.p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[220px]"
          data-testid="em-gallery-grid"
        >
          {g.photos.map((p, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className={`relative group overflow-hidden border border-white/10 ${SPANS[i]} ${HEIGHTS[i]}`}
              data-testid={`em-photo-${i}`}
            >
              <img
                src={PHOTOS[i % PHOTOS.length]}
                alt={p.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

              {/* Top zap badge */}
              <div
                className={`absolute top-4 ${isRtl ? "right-4" : "left-4"} h-9 w-9 bg-secondary/80 backdrop-blur-sm border border-white/15 flex items-center justify-center`}
              >
                <Zap className="h-4 w-4 text-primary" />
              </div>

              {/* Caption block */}
              <figcaption
                className={`absolute bottom-0 left-0 right-0 p-5 sm:p-6 ${
                  isRtl ? "text-right" : "text-left"
                }`}
              >
                <div className="text-[10px] tracking-[0.25em] uppercase text-primary font-semibold">
                  {p.tag}
                </div>
                <div className="mt-1.5 text-white text-lg sm:text-xl font-bold leading-snug">
                  {p.caption}
                </div>
                <div className="mt-3 h-px w-10 bg-primary group-hover:w-24 transition-all duration-500" />
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
